<script>
import { useAlert, useTrack } from 'dashboard/composables';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import InboxOptionMenu from './InboxOptionMenu.vue';
import InboxDisplayMenu from './InboxDisplayMenu.vue';

export default {
  components: {
    NextButton,
    ConfirmDialog,
    InboxOptionMenu,
    InboxDisplayMenu,
  },
  props: {
    isContextMenuOpen: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['redirect', 'filter'],
  data() {
    return {
      showInboxDisplayMenu: false,
      showInboxOptionMenu: false,
    };
  },
  watch: {
    isContextMenuOpen: {
      handler(val) {
        if (val) {
          this.showInboxDisplayMenu = false;
          this.showInboxOptionMenu = false;
        }
      },
      immediate: true,
    },
  },
  methods: {
    async markAllRead() {
      useTrack(INBOX_EVENTS.MARK_ALL_NOTIFICATIONS_AS_READ);
      try {
        await this.$store.dispatch('notifications/readAll');
        useAlert(this.$t('INBOX.ALERTS.MARK_ALL_READ'));
      } catch {
        useAlert(this.$t('INBOX.ALERTS.MARK_ALL_READ_ERROR'));
      }
    },
    async deleteAll() {
      const confirmed = await this.$refs.deleteAllDialog.showConfirmation();
      if (!confirmed) return;

      try {
        await this.$store.dispatch('notifications/deleteAll');
        useAlert(this.$t('INBOX.ALERTS.DELETE_ALL'));
        this.$emit('redirect');
      } catch {
        useAlert(this.$t('INBOX.ALERTS.DELETE_ALL_ERROR'));
      }
    },
    async deleteAllRead() {
      const confirmed = await this.$refs.deleteAllReadDialog.showConfirmation();
      if (!confirmed) return;

      try {
        await this.$store.dispatch('notifications/deleteAllRead');
        useAlert(this.$t('INBOX.ALERTS.DELETE_ALL_READ'));
        this.$emit('redirect');
      } catch {
        useAlert(this.$t('INBOX.ALERTS.DELETE_ALL_READ_ERROR'));
      }
    },
    openInboxDisplayMenu() {
      this.showInboxDisplayMenu = !this.showInboxDisplayMenu;
    },
    openInboxOptionsMenu() {
      this.showInboxOptionMenu = !this.showInboxOptionMenu;
    },
    onInboxOptionMenuClick(key) {
      const actions = {
        mark_all_read: () => this.markAllRead(),
        delete_all: () => this.deleteAll(),
        delete_all_read: () => this.deleteAllRead(),
      };
      this.showInboxOptionMenu = false;
      const action = actions[key];
      if (action) action();
    },
    onFilterChange(option) {
      this.$emit('filter', option);
      this.showInboxDisplayMenu = false;
      this.$emit('redirect');
    },
  },
};
</script>

<template>
  <header
    class="flex items-center w-full gap-2 px-3 py-2 border-b border-n-weak bg-n-solid-2"
  >
    <div class="flex items-center flex-1 min-w-0 gap-2">
      <h1 class="m-0 text-sm font-semibold truncate text-n-slate-12 min-w-0">
        {{ $t('INBOX.WORKSPACE.TITLE') }}
      </h1>
      <span
        v-if="unreadCount"
        :aria-label="$t('INBOX.WORKSPACE.UNREAD_COUNT', { count: unreadCount })"
        class="grid h-4 min-w-4 px-1 text-[10px] font-semibold text-white rounded-full bg-n-violet-9 place-items-center flex-shrink-0"
      >
        {{ unreadCount }}
      </span>
      <span class="text-xs truncate text-n-slate-10">
        {{ $t('INBOX.WORKSPACE.COUNT', { count: totalCount }) }}
      </span>
    </div>

    <div class="relative flex-shrink-0">
      <NextButton
        :label="$t('INBOX.LIST.DISPLAY_DROPDOWN')"
        icon="i-lucide-list-filter"
        slate
        sm
        :variant="showInboxDisplayMenu ? 'faded' : 'ghost'"
        @click="openInboxDisplayMenu"
      />
      <InboxDisplayMenu
        v-if="showInboxDisplayMenu"
        v-on-clickaway="openInboxDisplayMenu"
        class="absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0"
        @filter="onFilterChange"
      />
    </div>

    <div class="relative flex-shrink-0">
      <NextButton
        v-tooltip.bottom="$t('INBOX.WORKSPACE.NOTIFICATION_ACTIONS')"
        icon="i-lucide-ellipsis"
        slate
        sm
        :variant="showInboxOptionMenu ? 'faded' : 'ghost'"
        @click="openInboxOptionsMenu"
      />
      <InboxOptionMenu
        v-if="showInboxOptionMenu"
        v-on-clickaway="openInboxOptionsMenu"
        class="absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0"
        @option-click="onInboxOptionMenuClick"
      />
    </div>

    <ConfirmDialog
      ref="deleteAllDialog"
      :title="$t('INBOX.DELETE_ALL_CONFIRMATION.TITLE')"
      :description="$t('INBOX.DELETE_ALL_CONFIRMATION.MESSAGE')"
      :confirm-button-label="$t('INBOX.DELETE_ALL_CONFIRMATION.CONFIRM')"
      :cancel-button-label="$t('INBOX.DELETE_ALL_CONFIRMATION.CANCEL')"
    />
    <ConfirmDialog
      ref="deleteAllReadDialog"
      :title="$t('INBOX.DELETE_ALL_READ_CONFIRMATION.TITLE')"
      :description="$t('INBOX.DELETE_ALL_READ_CONFIRMATION.MESSAGE')"
      :confirm-button-label="$t('INBOX.DELETE_ALL_READ_CONFIRMATION.CONFIRM')"
      :cancel-button-label="$t('INBOX.DELETE_ALL_READ_CONFIRMATION.CANCEL')"
    />
  </header>
</template>
